import React, { useEffect, useState } from 'react';
import {
  TextField,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  LinearProgress,
} from '@mui/material';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import useGlynt from '../hooks/useGlynt';
import usePaginatedDataPools from '../hooks/usePaginatedDataPools';
import { AppContext } from '../../App';
import { IClient, IDataPool } from '../../types/my-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface ClientDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  client?: IClient | null;
  orgId: string;
  existingClients: IClient[];
  onClose: () => void;
}

function ClientDialog({
  open,
  mode,
  client,
  orgId,
  existingClients,
  onClose,
}: ClientDialogProps) {
  const { setToast } = React.useContext(AppContext);
  const glynt = useGlynt();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [abbr, setAbbr] = useState('');
  const [abbrError, setAbbrError] = useState('');
  const [description, setDescription] = useState('');
  const [selectedDataPools, setSelectedDataPools] = useState<Set<string>>(new Set());

  const dataPoolsQuery = usePaginatedDataPools(orgId, 500, 'label', open && mode === 'edit');
  const dataPools: IDataPool[] = dataPoolsQuery.data?.results ?? [];

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && client) {
        setName(client.name);
        setAbbr(client.abbr);
        setDescription(client.description || '');
        // Pre-select data pools assigned to this client
        const assigned = new Set(
          dataPools
            .filter((dp) => dp.client?.id === client.id)
            .map((dp) => dp.id)
        );
        setSelectedDataPools(assigned);
      } else {
        setName('');
        setAbbr('');
        setDescription('');
        setSelectedDataPools(new Set());
      }
      setNameError('');
      setAbbrError('');
    }
  }, [open, mode, client?.id]);

  // Update selected data pools when data pools load (for edit mode)
  useEffect(() => {
    if (open && mode === 'edit' && client && dataPools.length > 0) {
      const assigned = new Set(
        dataPools
          .filter((dp) => dp.client?.id === client.id)
          .map((dp) => dp.id)
      );
      setSelectedDataPools(assigned);
    }
  }, [dataPools.length]);

  // Validate name
  useEffect(() => {
    if (!open) return;
    if (name.trim().length === 0) {
      setNameError('Name is required.');
    } else if (
      existingClients.some(
        (c) => c.name.toLowerCase() === name.trim().toLowerCase() && c.id !== client?.id
      )
    ) {
      setNameError('Name already exists.');
    } else {
      setNameError('');
    }
  }, [name, existingClients, client?.id, open]);

  // Validate abbr
  useEffect(() => {
    if (!open) return;
    if (abbr.trim().length === 0) {
      setAbbrError('Abbreviation is required.');
    } else if (!/^[A-Z0-9]+$/.test(abbr)) {
      setAbbrError('Must be uppercase letters/numbers only.');
    } else if (abbr.length > 10) {
      setAbbrError('Max 10 characters.');
    } else if (
      existingClients.some(
        (c) => c.abbr.toLowerCase() === abbr.trim().toLowerCase() && c.id !== client?.id
      )
    ) {
      setAbbrError('Abbreviation already exists.');
    } else {
      setAbbrError('');
    }
  }, [abbr, existingClients, client?.id, open]);

  const createMutation = useMutation({
    mutationFn: async () =>
      (await glynt).clientApi.create({
        organization: orgId,
        name: name.trim(),
        abbr: abbr.trim(),
        description: description.trim(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setToast({ variant: 'success', open: true, message: 'Client created!' });
      onClose();
    },
    onError: () => {
      setToast({ variant: 'error', open: true, message: 'Failed to create client.' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!client) return;
      // Update client fields
      await (await glynt).clientApi.update(client.id, {
        name: name.trim(),
        abbr: abbr.trim(),
        description: description.trim(),
      });

      // Update data pool assignments
      const previouslyAssigned = new Set(
        dataPools.filter((dp) => dp.client?.id === client.id).map((dp) => dp.id)
      );

      const toAssign = [...selectedDataPools].filter((id) => !previouslyAssigned.has(id));
      const toUnassign = [...previouslyAssigned].filter((id) => !selectedDataPools.has(id));

      const g = await glynt;
      for (const dpId of toAssign) {
        await g.dataPool.update(dpId, { client: client.id });
      }
      for (const dpId of toUnassign) {
        await g.dataPool.update(dpId, { client: null });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['datapools'] });
      setToast({ variant: 'success', open: true, message: 'Client updated!' });
      onClose();
    },
    onError: () => {
      setToast({ variant: 'error', open: true, message: 'Failed to update client.' });
    },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const isSubmitDisabled =
    nameError.length > 0 ||
    abbrError.length > 0 ||
    name.trim().length === 0 ||
    abbr.trim().length === 0;

  const handleSubmit = () => {
    if (mode === 'create') {
      createMutation.mutate();
    } else {
      updateMutation.mutate();
    }
  };

  const handleAbbrChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAbbr(e.target.value.toUpperCase());
  };

  const toggleDataPool = (dpId: string) => {
    setSelectedDataPools((prev) => {
      const next = new Set(prev);
      if (next.has(dpId)) {
        next.delete(dpId);
      } else {
        next.add(dpId);
      }
      return next;
    });
  };

  return (
    <DialogGlynt
      open={open}
      closeModal={onClose}
      width="520px"
      cypressId={mode === 'create' ? 'dialog-create-client' : 'dialog-edit-client'}
      loading={isSaving}
    >
      <DialogTitleGlynt
        title={mode === 'create' ? 'Create Client' : `Edit Client — ${client?.name}`}
        closeModal={onClose}
        startIcon={mode === 'create' ? 'checkbox' : 'update'}
        loading={isSaving}
      />
      <DialogContentGlynt scrollable>
        <TextField
          autoFocus
          variant="outlined"
          size="small"
          label="Name"
          placeholder="Client name"
          value={name}
          disabled={isSaving}
          error={name.length > 0 && nameError.length > 0}
          onChange={(e) => setName(e.target.value)}
          helperText={name.length > 0 ? nameError : ''}
          fullWidth
          data-cy="input-client-name"
        />

        <TextField
          variant="outlined"
          size="small"
          label="Abbreviation"
          placeholder="ABBR"
          value={abbr}
          disabled={isSaving}
          error={abbr.length > 0 && abbrError.length > 0}
          onChange={handleAbbrChange}
          helperText={abbr.length > 0 && abbrError ? abbrError : 'Uppercase, max 10 characters'}
          fullWidth
          sx={{ mt: 2 }}
          inputProps={{ maxLength: 10 }}
          data-cy="input-client-abbr"
        />

        <TextField
          variant="outlined"
          size="small"
          label="Description"
          placeholder="Optional description"
          value={description}
          disabled={isSaving}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          sx={{ mt: 2 }}
          data-cy="input-client-description"
        />

        {mode === 'edit' && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Data Pool Assignment
            </Typography>
            {dataPoolsQuery.isLoading ? (
              <LinearProgress />
            ) : dataPools.length === 0 ? (
              <Typography sx={{ fontSize: '13px', color: '#5f6d7e' }}>
                No data pools in this organization.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {dataPools.map((dp) => {
                  const assignedToOther = dp.client && dp.client.id !== client?.id;
                  return (
                    <FormControlLabel
                      key={dp.id}
                      control={
                        <Checkbox
                          checked={selectedDataPools.has(dp.id)}
                          onChange={() => toggleDataPool(dp.id)}
                          disabled={isSaving || !!assignedToOther}
                          data-cy={`checkbox-dp-${dp.label}`}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '13px' }}>
                          {dp.label}
                          {assignedToOther && (
                            <Typography
                              component="span"
                              sx={{ fontSize: '12px', color: '#5f6d7e', ml: 1 }}
                            >
                              (assigned to {dp.client?.name})
                            </Typography>
                          )}
                        </Typography>
                      }
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={handleSubmit}
        closeModal={onClose}
        cancelCypressId="btn-client-cancel"
        submitCypressId="btn-client-submit"
        clearCypressId="btn-client-clear"
        submitButtonText={mode === 'create' ? 'Create' : 'Save'}
        submitLoading={isSaving}
        submitDisabled={isSubmitDisabled}
      />
    </DialogGlynt>
  );
}

export default ClientDialog;
